import findIndex from 'lodash/findIndex';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {connect} from 'react-redux';
import {compose} from 'redux';
import classNames from 'classnames';
import {window} from 'ssr-window';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import SearchUtils from 'search/search-utils';
import SHLoader from 'common/loader/SHLoader';
import {SEARCH_TYPE} from './search-prop-types';
import WithSearch from './WithSearch';
import SpotListContainer from './spot-list/SpotListContainer';

const SpotDetails = loadable(() => import('spot-details/SpotDetails'));
const SearchMap = loadable(() => import('./map/SearchMap'));
const DestinationInfoPanel = loadable(() =>
    import('destination/DestinationInfoPanel')
);

class SearchDesktop extends Component {
    static propTypes = {
        isMonthly: PropTypes.bool,
        sortBy: PropTypes.string,
        starts: PropTypes.string,
        ends: PropTypes.string,
        activeSearchTitle: PropTypes.string,
        spots: PropTypes.arrayOf(PropTypes.object),
        selectedSpot: PropTypes.object,
        searchType: SEARCH_TYPE.isRequired,
        city: PropTypes.object,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        event: PropTypes.object,
        searchRequestId: PropTypes.string,
        isInfoPanelVisible: PropTypes.bool,
        isEventListEnabled: PropTypes.bool,
        showEventList: PropTypes.bool,
        showSpotDetailsModal: PropTypes.bool,
        pushTo: PropTypes.func,
        findParkingNearMe: PropTypes.func,
        onToggleEventList: PropTypes.func.isRequired,
        onSpotDetailsHidden: PropTypes.func,
        isLowInventoryEnabled: PropTypes.bool,
        isSoldOutEnabled: PropTypes.bool,
        onShowDetailsClick: PropTypes.func.isRequired,
        onChangeSort: PropTypes.func.isRequired,
    };
    static defaultProps = {
        isInfoPanelVisible: true,
    };

    constructor(props) {
        super(props);

        const {searchType, city, destination, event} = props;

        this._hasInfoPanel = SearchUtils.hasInfoPanel({
            searchType,
            city,
            destination,
            event,
        });
    }

    componentDidMount() {
        if (!this.props.isSoldOutEnabled) {
            window.addEventListener('keydown', this._onKeyDown);
        }
    }

    componentDidUpdate(nextProps) {
        const {searchType, city, destination, event} = this.props;
        const {searchType: nextSearchType} = nextProps;

        if (searchType !== nextSearchType) {
            this._hasInfoPanel = SearchUtils.hasInfoPanel({
                nextSearchType,
                city,
                destination,
                event,
            });
        }
    }

    componentWillUnmount() {
        if (!this.props.isSoldOutEnabled) {
            window.removeEventListener('keydown', this._onKeyDown);
        }
    }

    _onKeyDown = evt => {
        const {spots, selectedSpot, pushTo} = this.props;

        if (!selectedSpot) {
            return;
        }

        const index = findIndex(spots, ['spotId', selectedSpot.spotId]);
        let nextSpot;

        if (evt.which === 40) {
            evt.preventDefault();

            nextSpot = spots[index + 1];
        } else if (evt.which === 38) {
            evt.preventDefault();

            nextSpot = spots[index - 1];
        }

        if (nextSpot) {
            pushTo(`/spot-details/${nextSpot.spotId}`, {referrer: 'list'});
        }
    };

    render() {
        const {
            sortBy,
            isMonthly,
            starts,
            ends,
            activeSearchTitle,
            isInfoPanelVisible,
            searchRequestId,
            spots,
            selectedSpot,
            findParkingNearMe,
            onSpotDetailsHidden,
            searchType,
            city,
            destination,
            event,
            showSpotDetailsModal,
            isEventListEnabled,
            showEventList,
            onToggleEventList,
            isLowInventoryEnabled,
            isSoldOutEnabled,
            onShowDetailsClick,
            onChangeSort,
        } = this.props;
        const classes = classNames('Search', {
            'Search-monthly': isMonthly,
            'Search-event-list': showEventList,
        });
        const showInfoPanel =
            !showSpotDetailsModal && this._hasInfoPanel && isInfoPanelVisible;

        return (
            <div className={classes}>
                {searchRequestId ? (
                    <div className="search-results">
                        <div className="SearchMap-container">
                            <SearchMap
                                spots={spots}
                                findParkingNearMe={findParkingNearMe}
                                isSoldOutEnabled={isSoldOutEnabled}
                                onShowDetailsClick={onShowDetailsClick}
                            />
                        </div>
                        <SpotListContainer
                            searchType={searchType}
                            sortBy={sortBy}
                            isMonthly={isMonthly}
                            starts={starts}
                            ends={ends}
                            activeSearchTitle={activeSearchTitle}
                            city={city}
                            destination={destination}
                            event={event}
                            spots={spots}
                            isEventListEnabled={isEventListEnabled}
                            showEventList={showEventList}
                            onToggleEventList={onToggleEventList}
                            onChangeSort={onChangeSort}
                            isLowInventoryEnabled={isLowInventoryEnabled}
                            isSoldOutEnabled={isSoldOutEnabled}
                            onShowDetailsClick={onShowDetailsClick}
                        />
                        {showSpotDetailsModal && (
                            <SpotDetails
                                spot={selectedSpot}
                                onCloseClick={onSpotDetailsHidden}
                                isLowInventoryEnabled={isLowInventoryEnabled}
                            />
                        )}
                        {showInfoPanel && searchType !== 'address' && (
                            <DestinationInfoPanel />
                        )}
                    </div>
                ) : (
                    <SHLoader />
                )}
            </div>
        );
    }
}

const mapStateToProps = ({
    city: {data: city},
    destination: {data: destination},
    event: {data: event},
    search: {
        data: {
            breadcrumbs: {
                activeBreadcrumb: {label: activeSearchTitle},
            },
            isInfoPanelVisible,
            searchType,
        },
    },
    searchRequest: {id: searchRequestId},
    spot: {showSpotDetailsModal},
}) => {
    return {
        city,
        destination,
        event,
        searchType,
        activeSearchTitle,
        isInfoPanelVisible,
        showSpotDetailsModal,
        searchRequestId,
    };
};

const enhance = compose(
    WithSearch,
    connect(mapStateToProps)
);

export default enhance(SearchDesktop);
